import { useEffect, useState, type FormEvent } from 'react';
import { Check, Plus } from 'lucide-react';
import { Button, Checkbox, Input, Select, Textarea, useToast } from '../ui';
import { Drawer } from '../ui/Drawer';
import { ListaTexto } from '../ui/ListaTexto';
import { CampoFoto } from '../ui/CampoFoto';
import { api, ApiError } from '../lib/api';
import { useResource } from '../lib/useResource';

export interface AgenteIA {
  key: string; name: string; description: string; enabled: boolean;
  isEntryPoint: boolean; intents: string[]; tools: string[]; capabilities: string[];
  systemPrompt: string;
  messages?: Mensagens;
  modelConfig: ConfiguracaoModelo;
  orchestrationConfig: ConfiguracaoOrquestracao;
  /** A listagem devolve só o indicador; a imagem vem de /:key/avatar. */
  hasAvatar?: boolean;
  /** Só existe no formulário: é o que se envia, nunca o que se lista. */
  avatarUrl?: string | null;
}

export interface ConfiguracaoModelo {
  provider: string; integrationId: string | null; model: string;
  reasoning: 'OFF' | 'LOW' | 'MEDIUM' | 'HIGH';
}
export interface ConfiguracaoOrquestracao {
  availableAgentKeys: string[];
  strategy: 'AUTOMATIC' | 'SEQUENTIAL' | 'FIRST_MATCH' | 'MANUAL';
  fallbackAgentKey: string | null;
}
interface IntegracaoIA { id:string; name:string; provider:string; kind:string; enabled:boolean; hasSecret:boolean; config?:{model?:unknown;models?:unknown} }

export interface Mensagens {
  welcome: string; clarification: string; processing: string;
  unavailable: string; documentReceived: string; humanHandoff: string;
}

/**
 * As seis mensagens são obrigatórias no schema (mínimo de 1 caractere cada) e
 * `.strict()` recusa o corpo se faltar uma. Criar um agente com seis campos
 * vazios obrigatórios é um formulário que ninguém termina.
 *
 * Estes textos são PADRÃO EDITÁVEL, não conteúdo escondido: aparecem
 * preenchidos no formulário, dentro de uma seção recolhida, e quem quiser
 * ajustar abre. A alternativa — mandar string genérica por baixo dos panos —
 * colocaria no WhatsApp do cliente uma frase que ninguém no time leu.
 */
export const MENSAGENS_PADRAO: Mensagens = {
  welcome: 'Olá! Sou o assistente virtual e vou te ajudar por aqui.',
  clarification: 'Só para eu não errar: pode me dar um pouco mais de detalhe?',
  processing: 'Um instante, estou verificando isso para você.',
  unavailable: 'Não consigo resolver isso agora. Vou chamar alguém do time.',
  documentReceived: 'Recebi o documento financeiro, obrigado. Já estou analisando.',
  humanHandoff: 'Passei sua conversa para um atendente. Em breve alguém responde por aqui.',
};

const ROTULO_MENSAGEM: Record<keyof Mensagens, { rotulo: string; quando: string }> = {
  welcome: { rotulo: 'Saudação', quando: 'Primeira resposta do agente na conversa.' },
  clarification: { rotulo: 'Pedido de detalhe', quando: 'Quando a mensagem do cliente é ambígua.' },
  processing: { rotulo: 'Processando', quando: 'Enquanto o agente consulta uma ferramenta.' },
  unavailable: { rotulo: 'Indisponível', quando: 'Quando o agente não consegue resolver.' },
  documentReceived: { rotulo: 'Arquivo recebido', quando: 'Confirmação ao receber documento ou imagem.' },
  humanHandoff: { rotulo: 'Transferência', quando: 'Ao passar a conversa para um operador.' },
};

const VAZIO: AgenteIA = {
  key: '', name: '', description: '', enabled: true, isEntryPoint: false,
  intents: [], tools: [], capabilities: [], systemPrompt: '',
  messages: MENSAGENS_PADRAO,
  modelConfig: { provider: 'OPENAI', integrationId: null, model: 'gpt-5-mini', reasoning: 'LOW' },
  orchestrationConfig: { availableAgentKeys: [], strategy: 'AUTOMATIC', fallbackAgentKey: null },
};

const PROVEDORES: Record<string,string> = {
  OPENAI:'OpenAI', ANTHROPIC:'Anthropic', GOOGLE_GEMINI:'Google Gemini',
  META_LLAMA:'Meta Llama', AZURE_OPENAI:'Azure OpenAI', CUSTOM:'Personalizado',
};
const MODELOS_PADRAO: Record<string,Array<{value:string;label:string}>> = {
  OPENAI:[
    {value:'gpt-5-mini',label:'GPT-5 mini'},
    {value:'gpt-5',label:'GPT-5'},
    {value:'gpt-5-nano',label:'GPT-5 nano'},
  ],
  ANTHROPIC:[
    {value:'claude-sonnet-4-5',label:'Claude Sonnet 4.5'},
    {value:'claude-haiku-4-5',label:'Claude Haiku 4.5'},
  ],
  GOOGLE_GEMINI:[
    {value:'gemini-2.5-pro',label:'Gemini 2.5 Pro'},
    {value:'gemini-2.5-flash',label:'Gemini 2.5 Flash'},
  ],
  META_LLAMA:[
    {value:'llama-4-maverick',label:'Llama 4 Maverick'},
    {value:'llama-4-scout',label:'Llama 4 Scout'},
  ],
  AZURE_OPENAI:[{value:'gpt-5-mini',label:'GPT-5 mini'}],
  CUSTOM:[],
};
const ESTRATEGIAS = [
  { value:'AUTOMATIC', label:'Automática — o orquestrador escolhe' },
  { value:'SEQUENTIAL', label:'Sequencial — respeita a ordem configurada' },
  { value:'FIRST_MATCH', label:'Primeiro compatível' },
  { value:'MANUAL', label:'Manual — não delega automaticamente' },
];

/**
 * Formulário de configuração do agente — criar e editar.
 *
 * A chave é imutável na edição: ela identifica o agente no roteamento e nos
 * registros de execução. Trocá-la não renomeia, cria outro agente e deixa o
 * histórico apontando para um que não existe mais.
 */
export function FormularioAgente({
  aberto, agente, onFechar, onSalvo,
}: {
  aberto: boolean;
  /** `null` = criar. */
  agente: AgenteIA | null;
  onFechar: () => void;
  onSalvo: () => void;
}) {
  const toast = useToast();
  const ferramentas = useResource<Array<{ id: string }>>('/ai-agents/tools', { vazio: () => false });
  const agentes = useResource<AgenteIA[]>('/ai-agents', { vazio: () => false });
  const integracoes = useResource<IntegracaoIA[]>('/integrations', { vazio: () => false });

  const [dados, setDados] = useState<AgenteIA>(agente ?? VAZIO);
  /* `undefined` = não mexeu na foto (o servidor mantém a atual);
     `null` = removeu; string = trocou. Um único estado não distinguiria
     "não mexi" de "apaguei", e a diferença decide se a foto some ao salvar
     qualquer outro campo. */
  const [foto, setFoto] = useState<string | null | undefined>(undefined);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Reabrir com outro agente precisa recarregar os valores; sem isto o
  // formulário guardaria o estado do anterior.
  useEffect(() => {
    if (aberto) { setDados(agente ?? VAZIO); setFoto(undefined); setErro(null); }
  }, [aberto, agente]);

  const editando = agente !== null;
  const mensagens = dados.messages ?? MENSAGENS_PADRAO;
  const modelosDisponiveis = (() => {
    const opcoes = new Map((MODELOS_PADRAO[dados.modelConfig.provider] ?? []).map((item) => [item.value, item]));
    if (dados.modelConfig.model && !opcoes.has(dados.modelConfig.model)) opcoes.set(dados.modelConfig.model, { value:dados.modelConfig.model, label:dados.modelConfig.model });
    if (integracoes.state.status === 'ready') {
      for (const integracao of integracoes.state.data.filter((item) => item.provider === dados.modelConfig.provider)) {
        const configurados = [integracao.config?.model, ...(Array.isArray(integracao.config?.models) ? integracao.config.models : [])];
        for (const model of configurados) if (typeof model === 'string' && model.trim() && !opcoes.has(model)) opcoes.set(model, { value:model, label:model });
      }
    }
    return [...opcoes.values()];
  })();

  function campo<K extends keyof AgenteIA>(chave: K, valor: AgenteIA[K]) {
    setDados((atual) => ({ ...atual, [chave]: valor }));
  }

  async function salvar(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setEnviando(true);
    setErro(null);
    const corpo = {
      name: dados.name.trim(),
      description: dados.description.trim(),
      capabilities: dados.capabilities,
      intents: dados.intents,
      tools: dados.tools,
      systemPrompt: dados.systemPrompt.trim(),
      messages: mensagens,
      modelConfig: dados.modelConfig,
      orchestrationConfig: dados.orchestrationConfig,
      enabled: dados.enabled,
      isEntryPoint: dados.isEntryPoint,
      ...(foto === undefined ? {} : { avatarUrl: foto }),
    };
    try {
      if (editando) {
        await api(`/ai-agents/${dados.key}`, { method: 'PATCH', body: corpo });
      } else {
        await api('/ai-agents', { method: 'POST', body: { key: dados.key.trim(), ...corpo } });
      }
      onSalvo();
      onFechar();
      toast(editando ? 'Agente atualizado.' : 'Agente criado.');
    } catch (falha) {
      setErro(falha instanceof ApiError ? falha.message : 'Falha de conexão.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Drawer
      open={aberto}
      onClose={onFechar}
      title={editando ? `Configurar ${agente.name}` : 'Novo agente de IA'}
      description={editando
        ? 'Identidade, comportamento e permissões deste agente.'
        : 'A chave identifica o agente no roteamento e não muda depois.'}
      footer={
        <>
          <Button variant="secondary" onClick={onFechar}>Cancelar</Button>
          <Button
            variant="primary"
            type="submit"
            form="form-agente"
            loading={enviando}
            icon={editando ? <Check size={18} aria-hidden="true" /> : <Plus size={18} aria-hidden="true" />}
          >
            {editando ? 'Salvar alterações' : 'Criar agente'}
          </Button>
        </>
      }
    >
      <form id="form-agente" onSubmit={salvar} className="form-agente">
        {erro && <p className="field__error" role="alert">{erro}</p>}

        <fieldset className="form-agente__grupo form-agente__grupo--duas">
          <legend className="field__label">Identidade</legend>

          <div className="largo">
          <CampoFoto
            label="Foto do agente"
            valor={foto === undefined
              ? (agente?.hasAvatar ? `/api/ai-agents/${agente.key}/avatar` : null)
              : foto}
            onChange={setFoto}
            hint="PNG, JPEG ou WebP, até 5 MB. A imagem é recortada em quadrado e reduzida para 512 px antes de salvar."
          />
          </div>

        <Input
          label="Chave"
          required
          readOnly={editando}
          value={dados.key}
          onChange={(e) => campo('key', e.currentTarget.value)}
          placeholder="triagem"
          hint={editando
            ? 'A chave não muda: ela identifica o agente no roteamento e no histórico de execuções.'
            : 'Minúsculas, sem espaço. Use ponto, hífen ou sublinhado para separar.'}
        />
        <Input
          label="Nome"
          required
          value={dados.name}
          onChange={(e) => campo('name', e.currentTarget.value)}
          hint="Como o agente aparece para o time. Não é mostrado ao cliente."
        />
        {/* Envolvido em vez de receber `className`: os campos não expõem
            escape de estilo de propósito — foi o `style=` inline solto que
            tornou o CSS do front anterior impossível de corrigir. Quem precisa
            de posicionamento usa o contêiner. */}
        <div className="largo">
          <Textarea
            label="Descrição"
            required
            rows={3}
            value={dados.description}
            onChange={(e) => campo('description', e.currentTarget.value)}
            hint="Entre 10 e 800 caracteres. O que este agente resolve, em uma frase."
          />
        </div>
        </fieldset>

        <fieldset className="form-agente__grupo">
          <legend className="field__label">Comportamento</legend>
        <Textarea
          label="Instrução do sistema"
          required
          rows={6}
          value={dados.systemPrompt}
          onChange={(e) => campo('systemPrompt', e.currentTarget.value)}
          hint="Mínimo de 20 caracteres. É o que define o comportamento do modelo — tom, limites e o que ele não deve fazer."
        />

        </fieldset>

        <fieldset className="form-agente__grupo form-agente__grupo--duas">
          <legend className="field__label">Modelo</legend>
          <Select
            label="Provedor"
            value={dados.modelConfig.provider}
            options={Object.entries(PROVEDORES).map(([value,label])=>({value,label}))}
            onChange={(e)=>{
              const provider=e.currentTarget.value;
              campo('modelConfig',{...dados.modelConfig,provider,integrationId:null,model:MODELOS_PADRAO[provider]?.[0]?.value??''});
            }}
            hint="A execução usa uma integração conectada deste provedor no workspace."
          />
          <Select
            label="Conexão"
            value={dados.modelConfig.integrationId??''}
            placeholder="Usar a conexão ativa mais recente"
            options={(integracoes.state.status==='ready'?integracoes.state.data:[])
              .filter((item)=>item.kind==='AI_AGENT'&&item.provider===dados.modelConfig.provider&&item.enabled&&item.hasSecret)
              .map((item)=>({value:item.id,label:item.name}))}
            onChange={(e)=>campo('modelConfig',{...dados.modelConfig,integrationId:e.currentTarget.value||null})}
            hint="Fixe uma conexão ou deixe o agente usar a conexão ativa mais recente."
          />
          {dados.modelConfig.provider === 'CUSTOM' ? (
            <Input
              label="Modelo"
              required
              value={dados.modelConfig.model}
              onChange={(e)=>campo('modelConfig',{...dados.modelConfig,model:e.currentTarget.value})}
              placeholder="Identificador do modelo"
              hint="Provedores personalizados exigem o identificador informado pela integração."
            />
          ) : (
            <Select
              label="Modelo"
              required
              value={dados.modelConfig.model}
              options={modelosDisponiveis}
              onChange={(e)=>campo('modelConfig',{...dados.modelConfig,model:e.currentTarget.value})}
              placeholder="Selecione o modelo"
              hint="A lista combina modelos recomendados, o valor já salvo e os configurados nas integrações do workspace."
            />
          )}
          <Select
            label="Raciocínio"
            value={dados.modelConfig.reasoning}
            options={[
              {value:'OFF',label:'Desativado'}, {value:'LOW',label:'Baixo'},
              {value:'MEDIUM',label:'Médio'}, {value:'HIGH',label:'Alto'},
            ]}
            onChange={(e)=>campo('modelConfig',{...dados.modelConfig,reasoning:e.currentTarget.value as ConfiguracaoModelo['reasoning']})}
            hint="Níveis maiores podem aumentar qualidade, latência e consumo."
          />
        </fieldset>

        <fieldset className="form-agente__grupo">
          <legend className="field__label">Orquestração</legend>
          <Select
            label="Estratégia"
            value={dados.orchestrationConfig.strategy}
            options={ESTRATEGIAS}
            onChange={(e)=>campo('orchestrationConfig',{...dados.orchestrationConfig,strategy:e.currentTarget.value as ConfiguracaoOrquestracao['strategy']})}
            hint="Define como este agente pode encaminhar a conversa para especialistas."
          />
          <div className="form-agente__subgrupo">
            <span className="field__label">Agentes disponíveis</span>
            <p className="field__hint">Somente estes especialistas podem ser acionados por este agente.</p>
            {(agentes.state.status==='ready'?agentes.state.data:[]).filter((item)=>item.key!==dados.key).map((item)=>(
              <Checkbox
                key={item.key}
                checked={dados.orchestrationConfig.availableAgentKeys.includes(item.key)}
                disabled={dados.orchestrationConfig.strategy==='MANUAL'}
                onChange={(e)=>campo('orchestrationConfig',{...dados.orchestrationConfig,availableAgentKeys:e.currentTarget.checked
                  ?[...dados.orchestrationConfig.availableAgentKeys,item.key]
                  :dados.orchestrationConfig.availableAgentKeys.filter((key)=>key!==item.key)})}
              >{item.name} <code>{item.key}</code></Checkbox>
            ))}
            {agentes.state.status==='ready'&&agentes.state.data.filter((item)=>item.key!==dados.key).length===0&&<p className="field__hint">Nenhum outro agente ativo neste workspace.</p>}
          </div>
          <Select
            label="Fallback"
            value={dados.orchestrationConfig.fallbackAgentKey??''}
            placeholder="Sem fallback — encaminhar para atendimento humano"
            options={(agentes.state.status==='ready'?agentes.state.data:[]).filter((item)=>item.key!==dados.key).map((item)=>({value:item.key,label:`${item.name} · ${item.key}`}))}
            onChange={(e)=>campo('orchestrationConfig',{...dados.orchestrationConfig,fallbackAgentKey:e.currentTarget.value||null})}
            hint="Usado quando nenhum especialista resolve ou o destino está indisponível. Ciclos são bloqueados ao salvar."
          />
        </fieldset>

        <fieldset className="form-agente__grupo">
          <legend className="field__label">Classificação</legend>
        <ListaTexto
          label="Habilidades"
          valores={dados.capabilities}
          onChange={(v) => campo('capabilities', v)}
          placeholder="Enter para adicionar"
          hint="Rótulos livres, usados para descrever o agente para o time."
        />
        <ListaTexto
          label="Intenções"
          valores={dados.intents}
          onChange={(v) => campo('intents', v)}
          placeholder="Enter para adicionar"
          hint="Assuntos que fazem o roteamento escolher este agente."
        />

        </fieldset>

        <fieldset className="form-agente__grupo">
          <legend className="field__label">Ferramentas</legend>
          {/* Lista fechada, vinda do registro do servidor. Campo livre aqui
              devolveria 400 "ferramenta não registrada" só no envio, depois de
              todo o formulário preenchido. */}
          <p className="field__hint">
            Ferramentas dão ao agente acesso a dados reais do cliente. Conceda apenas o necessário.
          </p>
          {ferramentas.state.status === 'ready' && ferramentas.state.data.length > 0 ? (
            ferramentas.state.data.map((f) => (
              <Checkbox
                key={f.id}
                checked={dados.tools.includes(f.id)}
                onChange={(e) => campo('tools', e.currentTarget.checked
                  ? [...dados.tools, f.id]
                  : dados.tools.filter((t) => t !== f.id))}
              >
                <code>{f.id}</code>
              </Checkbox>
            ))
          ) : (
            <p className="field__hint">Nenhuma ferramenta registrada nesta instalação.</p>
          )}
        </fieldset>

        {/* Recolhido por padrão: são seis campos já preenchidos com padrão
            razoável, e obrigar a passar por eles para criar um agente é o tipo
            de formulário que ninguém termina. */}
        <details className="form-agente__mensagens">
          <summary>Mensagens padrão do agente</summary>
          <p className="field__hint">
            Textos que o agente envia em situações fixas. Já vêm preenchidos e podem ser ajustados.
          </p>
          {(Object.keys(ROTULO_MENSAGEM) as Array<keyof Mensagens>).map((chave) => (
            <Textarea
              key={chave}
              label={ROTULO_MENSAGEM[chave].rotulo}
              hint={ROTULO_MENSAGEM[chave].quando}
              rows={2}
              required
              value={mensagens[chave]}
              onChange={(e) => campo('messages', { ...mensagens, [chave]: e.currentTarget.value })}
            />
          ))}
        </details>

        <fieldset className="form-agente__grupo">
          <legend className="field__label">Estado</legend>
          <Checkbox
            checked={dados.enabled}
            onChange={(e) => campo('enabled', e.currentTarget.checked)}
          >
            Ativo — participa do atendimento
          </Checkbox>
          <Checkbox
            checked={dados.isEntryPoint}
            onChange={(e) => {
              const marcado = e.currentTarget.checked;
              // O servidor recusa ponto de entrada inativo. Marcar aqui já
              // ativa, em vez de deixar a pessoa descobrir no envio.
              setDados((a) => ({ ...a, isEntryPoint: marcado, enabled: marcado ? true : a.enabled }));
            }}
          >
            Ponto de entrada — recebe toda mensagem nova e roteia para os outros
          </Checkbox>
          {dados.isEntryPoint && (
            <p className="field__hint">
              Só um agente deve ser ponto de entrada. Marcar este substitui o atual.
            </p>
          )}
        </fieldset>
      </form>
    </Drawer>
  );
}
